import * as _ from 'lodash';
import * as moment from 'moment';
import {resolveFieldValue} from '../../utils/field.util';
import {EntryFieldState, UpdateSource} from '../../constants/update-source.constant';
import {TimeCardType} from '../../types/timecard-type.type';
import {TimeCardStatusType} from '../../types/timecard-status.type';
import {TimeCardEntryType} from '../../types/timecard-entry-type.type';
import {TimeCardEntryLocationSourceType} from '../../types/timecard-entry-location-source-type.type';
import {TimeCardDataSourceType} from '../../types/timecard-data-source.type';
import {Stop, StopAdjustment, StopLocation, Task, WorkingSlot} from '../../types/timecard-detail-view.type';
import {AddStopDialogResult} from '../../types/timekeeping-dialog.type';
import {TimeCardModel} from '../models/timecard.model';
import {TimeCardDataModel} from '../models/timecard-data.model';
import {TimeCardGroup, TimeCardGroupStatus} from '../models/timecard-group.model';
import {TimeCardBreakModel} from '../models/timecard-break.model';
import {StateField, TimeCardEntryModel} from '../models/timecard-entry.model';
import {
  GEO_TIME_MISMATCH_ENTRY_RISKS,
  INFEASIBLE_ENTRY_RISKS,
  TimeCardAnalysis,
  TimeCardEntryAnalysisView,
  TimeCardEntryRisk
} from '../models/timecard-analysis.model';
import {
  TimecardAgentPerformance,
  TimecardAggregatedData,
  TimecardEditableField,
  TimecardEntryAnalysisMap,
  TimecardLeave,
  TimecardLocationPoint,
  TimecardLocationUpdatesByHour,
  TimecardShift
} from '../models/timecard-detail.model';
import {getAdminAllowedTransitions} from './timekeeping-status.util';
import {BreakType} from '../../../site-preferences/models/break-type.model';

// Timecard entries are loosely typed on the server side, so they are read and patched as plain objects.

/** Statuses that block Admin Time editing. */
export const LOCKED_ADMIN_TIME_STATUSES: TimeCardStatusType[] = [
  TimeCardStatusType.Approved,
  TimeCardStatusType.Completed,
  TimeCardStatusType.Archived
];

/** Meters in one degree of latitude — used to snap sensitive locations onto a coarse grid. */
const METERS_PER_LATITUDE_DEGREE = 111320;

export const DEFAULT_GENERALIZED_LOCATION_RADIUS_METERS = 1500;

export const EMPTY_AGGREGATED_DATA: TimecardAggregatedData = {
  totalDistance: 0,
  avgSpeed: 0,
  totalDriveTime: 0,
  totalServiceTime: 0,
  totalAdminTime: 0,
  totalPto: 0,
  totalPaidTime: 0,
  totalStops: '0 (0)',
  unplannedStops: 0,
  acknowledged: false
};

export const EMPTY_AGENT_PERFORMANCE: TimecardAgentPerformance = {
  agentRoutes: [],
  plannedRoutePolyline: '',
  agentIrregularity: null
};

// ******************************
//        Shifts and leaves
// ******************************

/** Status transitions an admin may pick. Approved is dropped for an infeasible timecard when the agent profile blocks it. */
export function buildStatusOptions(status: TimeCardStatusType, hasInfeasibility: boolean, blockApprovalOnInfeasible: boolean): TimeCardStatusType[] {
  const options = getAdminAllowedTransitions(status);
  return hasInfeasibility && blockApprovalOnInfeasible
    ? options.filter((option) => option !== TimeCardStatusType.Approved)
    : options;
}

export function mapShifts(timecards: TimeCardModel[], blockApprovalOnInfeasible: boolean, siteBreakTypes: BreakType[]): TimecardShift[] {
  const siteBreakTypeNames = _.mapValues(_.keyBy(siteBreakTypes, 'id'), 'name');
  return timecards
    .filter((timecard) => timecard.type !== TimeCardType.Leave)
    .map((timecard, index) => {
      // Rows show only what the agent reported; an open timecard without reports has no rows
      const reportedData = timecard.reportedData;
      const entries = reportedData?.entries || [];
      const stops = convertEntriesToNumberedStops(
        entries,
        timecard,
        buildEntryMapById((timecard as any).geoEventTrackingData?.entries),
        buildTaskReferenceMap(reportedData),
        buildEntryMapById(timecard.plannedData?.entries),
        buildBreakNameMap(timecard, entries, siteBreakTypeNames)
      );
      const statusOptions = buildStatusOptions(timecard.status, timecard.hasInfeasibility, blockApprovalOnInfeasible);

      return {
        id: timecard.id,
        shiftNumber: index + 1,
        shiftName: `Shift ${index + 1}`,
        clockInTime: resolveFieldValue(timecard.clockInTimeField),
        clockOutTime: resolveFieldValue(timecard.clockOutTimeField),
        status: timecard.status,
        isEditable: timecard.status === TimeCardStatusType.Closed,
        statusOptions,
        hasStatusOptions: statusOptions.length > 0,
        hasInfeasibility: timecard.hasInfeasibility,
        appUsed: timecard.appUsed,
        stops,
        totals: {
          totalStops: _.filter(stops, (stop) => stop.stopNumber != null).length,
          totalDistance: _.sumBy(stops, (stop) => stop.miles || 0),
          // The server total is not recalculated after a drive time edit, so it is summed from the rows
          totalDriveTime: _.sumBy(stops, (stop) => stop.driveTime || 0),
          // Other time totals are rendered as the server returns them, from the same reported data as the rows
          totalServiceTime: reportedData?.totals?.totalServiceTimeDurationMillis || 0,
          totalPlannedTime: timecard.plannedData?.totals?.totalServiceTimeDurationMillis || 0
        }
      };
    });
}

export function mapLeaves(timecards: TimeCardModel[]): TimecardLeave[] {
  return timecards
    .filter((timecard) => timecard.type === TimeCardType.Leave)
    .map((timecard) => {
      const absenceReason = (timecard as any).absenceReason;
      return {
        id: timecard.id,
        absenceReasonName: absenceReason?.name || '',
        payable: absenceReason ? absenceReason.payable : true,
        clockInTime: resolveFieldValue(timecard.clockInTimeField),
        clockOutTime: resolveFieldValue(timecard.clockOutTimeField),
        status: timecard.status
      };
    });
}

export function buildActualTrackingStops(timecards: TimeCardModel[]): any[] {
  return _.flatMap(timecards, (timecard) => {
    const entries = (timecard.actualTrackingData as any)?.entries || [];
    return entries.filter((entry: any) => entry.type === 'Stop' || entry.type === 'Unknown');
  });
}

export function buildWorkingSlots(timecards: TimeCardModel[]): WorkingSlot[] {
  return timecards.map((timecard, index) => ({
    id: `ws-${index + 1}`,
    agentId: timecard.agentId,
    in: {time: moment(resolveFieldValue(timecard.clockInTimeField) || 0).valueOf()},
    out: {time: moment(resolveFieldValue(timecard.clockOutTimeField) || 0).valueOf()}
  }));
}

export function buildAggregatedData(group: TimeCardGroup, shifts: TimecardShift[]): TimecardAggregatedData {
  const totals = group.totals;
  const reportedTotals = totals?.reportedTotals;
  const reportedStops = reportedTotals?.totalStopsCount || 0;
  const plannedStops = totals?.plannedTotals?.totalStopsCount || 0;
  const timecards = group.timeCards || [];

  // PTO is the clocked time of Leave timecards
  const totalPto = _.sumBy(timecards, (timecard) => {
    const clockIn = resolveFieldValue(timecard.clockInTimeField);
    const clockOut = resolveFieldValue(timecard.clockOutTimeField);
    return timecard.type === TimeCardType.Leave && clockIn && clockOut ? clockOut - clockIn : 0;
  });

  return {
    totalDistance: reportedTotals?.totalDistanceMeter || 0,
    avgSpeed: reportedTotals?.speedKmh || 0,
    // Matches the shifts' Totals rows, which include drive time edits
    totalDriveTime: _.sumBy(shifts, (shift) => shift.totals.totalDriveTime),
    totalServiceTime: reportedTotals?.totalServiceTimeDurationMillis || 0,
    totalAdminTime: totals?.totalAdminTime || 0,
    totalPto,
    totalPaidTime: _.sumBy(timecards, (timecard) => timecard.timeCardPaidTime?.totalTime || 0),
    totalStops: `${reportedStops} (${plannedStops})`,
    unplannedStops: Math.max(0, reportedStops - plannedStops),
    acknowledged: group.status === TimeCardGroupStatus.Approved || group.status === TimeCardGroupStatus.Completed
  };
}

/** Admin Time can be edited while at least one shift is not Completed or Archived. */
export function canEditAdminTime(shifts: TimecardShift[]): boolean {
  return _.some(shifts, (shift) => !_.includes(LOCKED_ADMIN_TIME_STATUSES, shift.status));
}

/**
 * Entry-level analysis by timecard id, then entry id. Entry ids are derived from the task id, so a bundle
 * task spanning several timecards has the same entry id in each of them - keying by entry id alone would
 * leak one timecard's risks into another.
 */
export function buildEntryAnalysisMap(analysisMap: Record<string, TimeCardAnalysis>): TimecardEntryAnalysisMap {
  return _.mapValues(analysisMap, (analysis) => {
    const timeCardEntries: Record<string, TimeCardEntryAnalysisView> = {};
    (analysis.reportedData?.entries || []).forEach((entryAnalysis) => {
      const allRisks = entryAnalysis.risks || [];
      const risks = _.reject(allRisks, (risk) => GEO_TIME_MISMATCH_ENTRY_RISKS.has(risk));
      const hasInfeasibleRisk = risks.some((risk) => INFEASIBLE_ENTRY_RISKS.has(risk));
      timeCardEntries[entryAnalysis.id] = {
        risks,
        hasInfeasibleRisk,
        hasWarningRisk: risks.length > 0 && !hasInfeasibleRisk,
        hasArrivalTimeMismatch: _.includes(allRisks, TimeCardEntryRisk.ArrivalTimeMismatch),
        hasDepartureTimeMismatch: _.includes(allRisks, TimeCardEntryRisk.DepartureTimeMismatch)
      };
    });
    return timeCardEntries;
  });
}

// ******************************
//        Stops
// ******************************

/**
 * Lookup of entries by id. Planned, geo-event and reported entries share the same id for a given stop
 * (all derived from the task id), so matching by id pairs them. Matching by array index is not safe:
 * the reported list routinely diverges (breaks, start/end location entries, archived, unplanned stops).
 */
function buildEntryMapById(entries: any[] | undefined): Map<string, any> {
  const map = new Map<string, any>();
  (entries || []).forEach((entry) => {
    if (entry.id) {
      map.set(entry.id, entry);
    }
  });
  return map;
}

/** taskId -> serviceId from the reported data's taskReferences. */
function buildTaskReferenceMap(reportedData: TimeCardDataModel | undefined): Map<string, string> {
  const map = new Map<string, string>();
  (reportedData?.taskReferences || []).forEach((ref: any) => {
    if (ref.id && ref.serviceId) {
      map.set(ref.id, ref.serviceId);
    }
  });
  return map;
}

function resolveServiceIds(taskIds: string[] | undefined, taskRefMap?: Map<string, string>): string[] {
  if (!taskIds || !taskRefMap) return [];
  return _.compact(taskIds.map((taskId) => taskRefMap.get(taskId)));
}

/** Task ids covered by an entry. The server sends them as entityIds; taskIds is the legacy name. */
function getEntryTaskIds(entry: any): string[] {
  return entry.entityIds || entry.taskIds || [];
}

/**
 * Break type names by Break entry id. A Break entry shares its id with the timecard break.
 * Order: the break's breakTypeName, the entry's breakReference name, then the site break types by type id.
 */
function buildBreakNameMap(timecard: TimeCardModel, entries: TimeCardEntryModel[],
                           siteBreakTypeNames: Record<string, string>): Map<string, string> {
  const breaksById = _.keyBy(resolveFieldValue<TimeCardBreakModel[]>(timecard.breaksField), 'id');
  const map = new Map<string, string>();
  _.forEach(entries, (entry) => {
    if (entry.type !== TimeCardEntryType.Break) return;
    const timecardBreak = breaksById[entry.id];
    const breakTypeId = entry.breakReference?.id || timecardBreak?.breakTypeId;
    map.set(entry.id, timecardBreak?.breakTypeName || entry.breakReference?.name || siteBreakTypeNames[breakTypeId]);
  });
  return map;
}

/**
 * Task ids that belong to BundleTask entries, so separate entries for the same tasks can be skipped and
 * the bundle is shown as a single stop. The server already drops bundled tasks from reported and geo-event
 * data; plannedData is built from the route task list without that filter, so the guard is kept for it.
 */
function buildBundledTaskIdSet(entries: any[]): Set<string> {
  const bundledTaskIds = new Set<string>();
  entries.forEach((entry) => {
    if (entry.archive || entry.type !== TimeCardEntryType.BundleTask) return;
    getEntryTaskIds(entry).forEach((taskId) => bundledTaskIds.add(taskId));
  });
  return bundledTaskIds;
}

function isEntryInBundle(entry: any, bundledTaskIds: Set<string>): boolean {
  if (!bundledTaskIds.size || entry.type !== TimeCardEntryType.Task) return false;
  const taskIds = getEntryTaskIds(entry);
  return taskIds.length
    ? _.every(taskIds, (taskId) => bundledTaskIds.has(taskId))
    : bundledTaskIds.has(entry.id);
}

export function isSensitiveLocationEntry(entry: any): boolean {
  return entry.type === TimeCardEntryType.Break
    || entry.type === TimeCardEntryType.AgentStartLocation
    || entry.type === TimeCardEntryType.AgentEndLocation;
}

/**
 * Stops with numbering that matches the map markers and the Agent Portal: Start (AgentStartLocation) is 0,
 * every other entry, breaks included, is 1, 2, 3… in time order. Archived entries and entries inside a bundle
 * are hidden.
 */
function convertEntriesToNumberedStops(entries: any[], timecard: TimeCardModel, geoEntryMap: Map<string, any>,
                                       taskRefMap: Map<string, string>, plannedEntryMap: Map<string, any>,
                                       breakNameMap: Map<string, string>): Stop[] {
  let stopCounter = 0;
  const stops: Stop[] = [];
  const bundledTaskIds = buildBundledTaskIdSet(entries);
  entries.forEach((entry, entryIndex) => {
    if (entry.archive || isEntryInBundle(entry, bundledTaskIds)) return;
    const stop = convertEntryToStop(entry, entryIndex, timecard, geoEntryMap, taskRefMap, plannedEntryMap, breakNameMap);
    if (entry.type === TimeCardEntryType.AgentStartLocation) {
      stop.stopNumber = 0;
    } else {
      stopCounter++;
      stop.stopNumber = stopCounter;
    }
    stops.push(stop);
  });
  return stops;
}

function convertEntryToStop(entry: any, index: number, timecard: TimeCardModel, geoEntryMap: Map<string, any>,
                            taskRefMap: Map<string, string>, plannedEntryMap: Map<string, any>,
                            breakNameMap: Map<string, string>): Stop {
  const locationValue = resolveFieldValue<any>(entry.location) || {};
  const serviceTimeObj = resolveFieldValue<any>(entry.serviceTime);

  // GPS times come from the geoEventTrackingData entry with the same id
  const geoEntry = geoEntryMap.get(entry.id);
  const isBreak = entry.type === TimeCardEntryType.Break;
  // A break shows and edits its duration in the Service Time column
  const serviceTimeField = isBreak ? entry.duration : entry.serviceTime;
  const isSensitive = isSensitiveLocationEntry(entry);
  const isAgentLocationEntry = entry.type === TimeCardEntryType.AgentStartLocation
    || entry.type === TimeCardEntryType.AgentEndLocation;
  const isCustomTask = entry.type === TimeCardEntryType.CustomTask;
  const isEndLocationEntry = entry.type === TimeCardEntryType.AgentEndLocation;
  // The End stop shows clock-out as its Arrival only; older time cards keep it in endTime.
  const arrivalTimeField = resolveEndLocationArrivalField(entry, isEndLocationEntry);
  const arrivalField = entry[arrivalTimeField];
  const departureField = isEndLocationEntry ? null : entry.endTime;
  const geoArrivalField = geoEntry ? geoEntry[resolveEndLocationArrivalField(geoEntry, isEndLocationEntry)] : null;
  const geoDepartureField = geoEntry && !isEndLocationEntry ? geoEntry.endTime : null;

  let stopName: string;
  if (isBreak) {
    stopName = breakNameMap.get(entry.id) || 'Break';
  } else if (entry.type === TimeCardEntryType.AgentStartLocation) {
    stopName = 'Start';
  } else if (entry.type === TimeCardEntryType.AgentEndLocation) {
    stopName = 'End';
  } else if (isCustomTask) {
    // The server names it after its account or service point; a custom address has no name
    stopName = entry.locationSource?.name || 'Custom Task';
  } else {
    stopName = entry.accountReference?.name || `Stop ${index + 1}`;
  }

  // For bundle entries the server already aggregates the bundled tasks' service time into the bundle
  // task's serviceTime, so generatedTotalTime covers all tasks in the bundle.
  const plannedEntry = plannedEntryMap.get(entry.id);
  // Passive tracking is matched by array index, as before (see buildEntryMapById on why that is fragile).
  const passiveEntry = (timecard as any).passiveTrackingData?.entries?.[index];

  const initialLocationValue = entry.location?.initial?.value || {};
  const formattedAddress = isSensitive
    ? buildGeneralizedLocationDisplay(locationValue)
    : buildFormattedAddressFromLocation(locationValue);
  const taskIds = getEntryTaskIds(entry);
  const serviceIds = resolveServiceIds(taskIds, taskRefMap);
  const initialLocation: StopLocation = {
    lat: initialLocationValue.lat || 0,
    lng: initialLocationValue.lng || 0,
    address: initialLocationValue.address,
    formattedAddress: isSensitive
      ? buildGeneralizedLocationDisplay(initialLocationValue)
      : buildFormattedAddressFromLocation(initialLocationValue)
  };

  return {
    id: entry.id || `stop-${index + 1}`,
    name: stopName,
    externalId: entry.accountReference?.externalId || '',
    address: formattedAddress,
    // Meters; a break is not a journey
    miles: isBreak ? null : (resolveFieldValue<number>(entry.drivingDistanceFromPrevStop) || 0),
    driveTime: resolveFieldValue<number>(entry.drivingTimeFromPrevStop) || 0,
    arrival: resolveFieldValue<number>(arrivalField) || null,
    arrivalGps: resolveFieldValue<number>(geoArrivalField) || null,
    departure: resolveFieldValue<number>(departureField) || null,
    depGps: resolveFieldValue<number>(geoDepartureField) || null,
    // For breaks the duration is shown as the service time
    serviceTime: isBreak ? (resolveFieldValue<number>(entry.duration) || 0) : (serviceTimeObj?.generatedTotalTime || 0),
    paidTime: entry.paidTime || 0,
    flag: false,
    miss: false,
    location: {
      lat: locationValue.lat || 0,
      lng: locationValue.lng || 0,
      address: locationValue.address,
      formattedAddress
    },
    // References for edit functionality
    timecardId: timecard.id,
    entryIndex: index,
    plannedServiceTime: plannedEntry?.serviceTime?.initial?.value?.generatedTotalTime || 0,
    passiveTrackingServiceTime: passiveEntry?.serviceTime?.initial?.value?.generatedTotalTime || 0,
    plannedDistance: plannedEntry ? resolveFieldValue<number>(plannedEntry.drivingDistanceFromPrevStop) : null,
    plannedArrival: plannedEntry ? resolveFieldValue<number>(plannedEntry.startTime) : null,
    plannedDeparture: plannedEntry ? resolveFieldValue<number>(plannedEntry.endTime) : null,
    hasPlannedData: !isBreak && !isAgentLocationEntry && !!plannedEntry,
    isArrivalAdjusted: arrivalField?.state === EntryFieldState.Adjusted,
    arrivalAdjustmentSource: arrivalField?.adjusted?.updatedBy?.source,
    isDepartureAdjusted: departureField?.state === EntryFieldState.Adjusted,
    departureAdjustmentSource: departureField?.adjusted?.updatedBy?.source,
    isServiceTimeAdjusted: serviceTimeField?.state === EntryFieldState.Adjusted,
    serviceTimeAdjustmentSource: serviceTimeField?.adjusted?.updatedBy?.source,
    isLocationAdjusted: entry.location?.state === EntryFieldState.Adjusted,
    locationAdjustmentSource: entry.location?.adjusted?.updatedBy?.source,
    isDistanceAdjusted: !isBreak && entry.drivingDistanceFromPrevStop?.state === EntryFieldState.Adjusted,
    distanceAdjustmentSource: isBreak ? undefined : entry.drivingDistanceFromPrevStop?.adjusted?.updatedBy?.source,
    arrivalAdjustment: buildAdjustment(arrivalField),
    departureAdjustment: buildAdjustment(departureField),
    serviceTimeAdjustment: buildAdjustment(serviceTimeField),
    locationAdjustment: buildAdjustment(entry.location),
    distanceAdjustment: isBreak ? undefined : buildAdjustment(entry.drivingDistanceFromPrevStop),
    initialArrival: arrivalField?.initial?.value || null,
    initialDeparture: departureField?.initial?.value || null,
    initialServiceTime: isBreak
      ? (entry.duration?.initial?.value || 0)
      : (entry.serviceTime?.initial?.value?.generatedTotalTime || 0),
    initialLocation,
    initialDistance: entry.drivingDistanceFromPrevStop?.initial?.value || 0,
    // Entry-level timezone only. Times are displayed in the user's selected timezone, so this is used
    // solely to flag - and tooltip - a stop whose local zone differs.
    zoneId: locationValue.zoneId || undefined,
    type: entry.type,
    archive: entry.archive,
    isAgentLocationEntry,
    isStartLocationEntry: entry.type === TimeCardEntryType.AgentStartLocation,
    isEndLocationEntry,
    arrivalTimeField,
    isBreakEntry: isBreak,
    isCustomTaskEntry: isCustomTask,
    isSensitiveLocationEntry: isSensitive,
    // A CustomTask location is always CUSTOM on the server, whatever it was taken from
    locationType: (isCustomTask && entry.locationSource?.type) || locationValue.locationType || locationValue.address?.locationType,
    taskIds,
    serviceIds,
    // Without resolved services the bundle has nothing to expand, so it is shown as a plain stop
    isBundle: entry.type === TimeCardEntryType.BundleTask && serviceIds.length > 0,
    isBundleExpanded: false
  };
}

/** The adjusted value is overwritten on every edit, so its updatedBy/updatedAt always describe the latest one. */
function buildAdjustment(field: StateField<unknown> | undefined): StopAdjustment | undefined {
  const adjusted = field?.state === EntryFieldState.Adjusted ? field.adjusted : undefined;
  return adjusted?.updatedBy?.name && adjusted.updatedAt
    ? {updatedBy: adjusted.updatedBy.name, updatedAt: adjusted.updatedAt}
    : undefined;
}

/** Clock-out moved from the End stop's endTime to its startTime; older time cards only have endTime. */
function resolveEndLocationArrivalField(entry: any, isEndLocationEntry: boolean): 'startTime' | 'endTime' {
  return isEndLocationEntry && !resolveFieldValue(entry.startTime) && resolveFieldValue(entry.endTime)
    ? 'endTime'
    : 'startTime';
}

// ******************************
//        Locations
// ******************************

/** City, state and zip only — used for Start/End/Break location privacy in the table. */
function buildGeneralizedLocationDisplay(locationValue: any): string {
  if (!locationValue) return '';
  const address = locationValue.address || locationValue;
  return _.compact([address.city, address.state, address.zipCode]).join(', ');
}

/** Prefers formattedAddress, then builds from address components, then falls back to coordinates. */
function buildFormattedAddressFromLocation(locationValue: any): string {
  if (!locationValue) return '';
  if (locationValue.formattedAddress) return locationValue.formattedAddress;
  if (locationValue.address?.formattedAddress) return locationValue.address.formattedAddress;

  const address = locationValue.address || locationValue;
  const streetPart = _.compact([address.house, address.street]).join(' ');
  const parts = _.compact([streetPart, address.city, address.state, address.zipCode, address.country]);
  if (parts.length) {
    return parts.join(', ');
  }

  if (locationValue.lat && locationValue.lng) {
    return `${locationValue.lat.toFixed(4)}, ${locationValue.lng.toFixed(4)}`;
  }
  return '';
}

/**
 * Approximate coordinates for Start/End/Break entries. Coordinates already generalized by the server win;
 * otherwise the exact ones are snapped onto a grid the size of the displayed radius, so the circle center
 * never sits on the agent's real location.
 */
function getGeneralizedMapLocation(locationValue: any, radiusMeters: number): { lat: number; lng: number } | null {
  if (!locationValue) return null;

  const generalized = locationValue.generalizedLocation || locationValue.generalized;
  const generalizedLat = generalized?.lat ?? locationValue.generalizedLat;
  const generalizedLng = generalized?.lng ?? locationValue.generalizedLng;
  if (generalizedLat && generalizedLng) {
    return {lat: generalizedLat, lng: generalizedLng};
  }

  if (!locationValue.lat || !locationValue.lng) {
    return null;
  }
  return snapLocationToGrid(locationValue.lat, locationValue.lng, radiusMeters);
}

/** Move a coordinate to the center of its grid cell — the same input always gives the same cell. */
function snapLocationToGrid(lat: number, lng: number, cellSizeMeters: number): { lat: number; lng: number } {
  const latCellDegrees = cellSizeMeters / METERS_PER_LATITUDE_DEGREE;
  const snappedLat = (Math.floor(lat / latCellDegrees) + 0.5) * latCellDegrees;
  // Longitude cells shrink towards the poles; deriving them from the snapped latitude keeps every point
  // of the same cell row on one grid.
  const lngCellDegrees = latCellDegrees / Math.max(Math.cos(snappedLat * Math.PI / 180), 0.01);
  return {
    lat: snappedLat,
    lng: (Math.floor(lng / lngCellDegrees) + 0.5) * lngCellDegrees
  };
}

/** Circles drawn on the map instead of exact Start/End/Break markers. */
export function buildGeneralizedLocationCircles(timecard: TimeCardModel, radiusMeters: number): Record<string, unknown>[] {
  const entries = timecard.reportedData?.entries || [];
  return _.compact(entries.map((entry: any) => {
    if (entry.archive || !isSensitiveLocationEntry(entry)) {
      return null;
    }
    const mapLocation = getGeneralizedMapLocation(resolveFieldValue(entry.location) || {}, radiusMeters);
    if (!mapLocation) {
      return null;
    }
    return {
      center: {latitude: mapLocation.lat, longitude: mapLocation.lng},
      radius: radiusMeters,
      stroke: {color: '#646464', weight: 2, opacity: 0.8},
      fill: {color: '#646464', opacity: 0.15},
      geodesic: false,
      draggable: false,
      clickable: false,
      editable: false,
      visible: true,
      control: {}
    };
  }));
}

/** Purpose decides the marker color: Operation (Field Service) green, Pickup orange, Dropoff blue. */
function getEntryPurpose(entry: any): string {
  if (entry.purpose) return entry.purpose;
  if (entry.taskPurpose) return entry.taskPurpose;
  if (entry.accountReference?.purpose) return entry.accountReference.purpose;

  switch (entry.type) {
    case TimeCardEntryType.AgentStartLocation:
      return 'StartDay';
    case TimeCardEntryType.AgentEndLocation:
      return 'EndDay';
    default:
      return 'Operation';
  }
}

/** Map tasks of one timecard. Start/End/Break markers are hidden and placed on the generalized location. */
export function buildMapTasks(timecard: TimeCardModel, generalizedRadiusMeters: number): Task[] {
  const entries = timecard.reportedData?.entries || [];
  const bundledTaskIds = buildBundledTaskIdSet(entries);
  const tasks: Task[] = [];
  let stopCounter = 0;

  entries.forEach((entry: any, index: number) => {
    if (entry.archive || isEntryInBundle(entry, bundledTaskIds)) return;

    const isSensitive = isSensitiveLocationEntry(entry);
    let daySequence: number;
    if (entry.type === TimeCardEntryType.AgentStartLocation) {
      daySequence = 0;
    } else {
      stopCounter++;
      daySequence = stopCounter;
    }

    const location = resolveFieldValue<any>(entry.location) || {};
    const mapLocation = isSensitive
      ? getGeneralizedMapLocation(location, generalizedRadiusMeters)
      : {lat: location.lat || location.address?.location?.lat, lng: location.lng || location.address?.location?.lng};
    if (!mapLocation?.lat || !mapLocation?.lng) {
      return;
    }

    const startTime = resolveFieldValue<number>(entry.startTime) || Date.now();
    const endTime = resolveFieldValue<number>(entry.endTime) || Date.now();
    const entryAddress = location.address || entry.accountReference?.address || {};
    const taskAddress = isSensitive
      ? _.pick(entryAddress, ['city', 'state', 'zipCode', 'country'])
      : entryAddress;
    const formattedAddress = isSensitive
      ? buildGeneralizedLocationDisplay(location)
      : (entryAddress.formattedAddress || buildFormattedAddressFromLocation(location));

    tasks.push({
      id: entry.id || `task-${timecard.id}-${index + 1}`,
      agentId: timecard.agentId,
      serviceId: entry.accountId || `service-${index + 1}`,
      status: 'Completed',
      taskType: entry.type || 'Service',
      hideMapMarker: isSensitive,
      routeId: timecard.id,
      agentHeadingTime: startTime - (15 * 60 * 1000),
      agentArrivalTime: startTime,
      agentDepartureTime: endTime,
      statusUpdatedAt: endTime,
      accountReference: entry.accountReference,
      routeAnalysis: {daySequence},
      taskSpec: {
        purpose: getEntryPurpose(entry),
        address: {
          ...taskAddress,
          location: {lat: mapLocation.lat, lng: mapLocation.lng},
          formattedAddress
        }
      }
    });
  });

  return tasks;
}

// ******************************
//        Agent performance
// ******************************

function getLocationUpdates(performance: TimecardAgentPerformance): TimecardLocationPoint[] {
  return _.flatMap(performance?.agentRoutes, (route) => (route as { locationUpdates?: TimecardLocationPoint[] }).locationUpdates || []);
}

/** Location updates bucketed by local hour (1-24), the way the timeline looks them up. */
export function buildLocationUpdatesByHour(performance: TimecardAgentPerformance): TimecardLocationUpdatesByHour {
  const byHour: TimecardLocationUpdatesByHour = {};
  for (let hour = 1; hour <= 24; hour++) {
    byHour[hour] = [];
  }
  getLocationUpdates(performance).forEach((item) => {
    byHour[new Date(item.timestamp).getHours() + 1].push(item);
  });
  return byHour;
}

export function getLatestLocation(performance: TimecardAgentPerformance): TimecardLocationPoint | undefined {
  return _.maxBy(getLocationUpdates(performance), 'timestamp');
}

/** Location update closest to a timeline timestamp, looked up within the same local hour. */
export function findClosestLocation(byHour: TimecardLocationUpdatesByHour, timestamp: number): TimecardLocationPoint | undefined {
  const candidates = byHour?.[new Date(timestamp).getHours() + 1];
  return _.minBy(candidates, (item) => Math.abs(item.timestamp - timestamp));
}

// ******************************
//        Update payloads
// ******************************

/** Adjusted value of a field. No updatedBy: the server stamps the current editor, so a previous one is not carried over. */
function buildAdjustedPayload(value: unknown): any {
  return {value, source: UpdateSource.Manual};
}

function setAdjusted(field: any, value: unknown): void {
  field.adjusted = buildAdjustedPayload(value);
  field.state = EntryFieldState.Adjusted;
}

/** The whole timecard is sent back on every edit; the clone is patched and posted. */
export function cloneTimecardForUpdate(timecard: TimeCardModel): any {
  return _.cloneDeep(timecard);
}

function ensureReportedEntries(payload: any, timecard: TimeCardModel): any[] {
  if (!payload.reportedData) {
    payload.reportedData = {
      id: `reported-${timecard.id}`,
      source: TimeCardDataSourceType.Reported,
      entries: [],
      lastDataUpdatedAt: Date.now()
    };
  }
  if (!payload.reportedData.entries) {
    payload.reportedData.entries = [];
  }
  return payload.reportedData.entries;
}

/** Start location first, then the other entries by start time, then end location. */
function sortEntries(entries: any[]): any[] {
  const isStart = (entry: any) => entry.type === TimeCardEntryType.AgentStartLocation;
  const isEnd = (entry: any) => entry.type === TimeCardEntryType.AgentEndLocation;
  const others = _.sortBy(entries.filter((entry) => !isStart(entry) && !isEnd(entry)),
    (entry) => resolveFieldValue<number>(entry.startTime) || 0);
  return [...entries.filter(isStart), ...others, ...entries.filter(isEnd)];
}

/** Patch one field of a reported entry in place. */
export function applyEntryFieldUpdate(entry: any, field: TimecardEditableField, value: any): void {
  const isBreak = entry.type === TimeCardEntryType.Break;

  switch (field) {
    case 'arrival': {
      const arrivalField = entry[resolveEndLocationArrivalField(entry, entry.type === TimeCardEntryType.AgentEndLocation)];
      if (arrivalField) {
        setAdjusted(arrivalField, value);
      }
      // For Break entries, duration = endTime - startTime
      if (isBreak && entry.duration) {
        const endTime = resolveFieldValue<number>(entry.endTime);
        if (endTime) {
          setAdjusted(entry.duration, endTime - value);
        }
      }
      break;
    }
    case 'departure':
      if (entry.endTime) {
        setAdjusted(entry.endTime, value);
      }
      if (isBreak && entry.duration) {
        const startTime = resolveFieldValue<number>(entry.startTime);
        if (startTime) {
          setAdjusted(entry.duration, value - startTime);
        }
      }
      break;
    case 'serviceTime':
      if (isBreak) {
        // Break entries use duration instead of serviceTime
        if (entry.duration) {
          setAdjusted(entry.duration, value);
        }
      } else if (entry.serviceTime) {
        const currentValue = entry.serviceTime.adjusted?.value || entry.serviceTime.initial?.value || {
          entryTime: 0, exitTime: 0, workTime: 0, subTasksTime: 0, generatedTotalTime: 0
        };
        const workTime = Math.max(0, value - (currentValue.entryTime || 0) - (currentValue.exitTime || 0) - (currentValue.subTasksTime || 0));
        setAdjusted(entry.serviceTime, {...currentValue, workTime, generatedTotalTime: value});
      }
      break;
    case 'distance':
      if (!entry.drivingDistanceFromPrevStop) {
        entry.drivingDistanceFromPrevStop = {initial: {value: 0}, adjusted: null, state: EntryFieldState.Initial};
      }
      setAdjusted(entry.drivingDistanceFromPrevStop, value);
      break;
  }
}

/** Payload with a new Task entry, sorted into place. The server generates the entry id. */
export function buildAddTaskPayload(timecard: TimeCardModel, stopData: AddStopDialogResult): any {
  const payload = cloneTimecardForUpdate(timecard);
  const entries = ensureReportedEntries(payload, timecard);
  const manual = (value: unknown) => ({adjusted: {value, source: UpdateSource.Manual}, state: EntryFieldState.Adjusted});
  const duration = stopData.departure - stopData.arrival;
  const serviceTime = stopData.serviceTime || duration;

  entries.push({
    accountId: stopData.accountId,
    accountReference: {id: stopData.accountId, name: stopData.accountName},
    type: TimeCardEntryType.Task,
    startTime: manual(stopData.arrival),
    endTime: manual(stopData.departure),
    duration: manual(duration),
    serviceTime: manual({entryTime: 0, exitTime: 0, workTime: serviceTime, subTasksTime: 0, generatedTotalTime: serviceTime}),
    location: manual({
      lat: stopData.location.lat,
      lng: stopData.location.lng,
      locationType: 'ACCOUNT',
      id: stopData.accountId,
      address: stopData.location.address,
      formattedAddress: stopData.location.formattedAddress
    }),
    drivingTimeFromPrevStop: manual(0),
    drivingDistanceFromPrevStop: manual(0)
  });
  payload.reportedData.entries = sortEntries(entries);
  return payload;
}

/**
 * Payload with a new CustomTask entry, sorted into place. Only a custom address is sent: the server
 * takes an account's or service point's address, and its name, from the entity itself.
 */
export function buildAddCustomTaskPayload(timecard: TimeCardModel, stopData: AddStopDialogResult): any {
  const payload = cloneTimecardForUpdate(timecard);
  const entries = ensureReportedEntries(payload, timecard);
  const manual = (value: unknown) => ({adjusted: {value, source: UpdateSource.Manual}, state: EntryFieldState.Adjusted});
  const duration = stopData.departure - stopData.arrival;
  const serviceTime = stopData.serviceTime || duration;
  const isCustomAddress = stopData.locationSource.type === TimeCardEntryLocationSourceType.CUSTOM;

  entries.push({
    type: TimeCardEntryType.CustomTask,
    locationSource: stopData.locationSource,
    ...(isCustomAddress ? {location: manual(buildCustomLocationValue(stopData.location))} : {}),
    startTime: manual(stopData.arrival),
    endTime: manual(stopData.departure),
    duration: manual(duration),
    serviceTime: manual({entryTime: 0, exitTime: 0, workTime: serviceTime, subTasksTime: 0, generatedTotalTime: serviceTime}),
    drivingTimeFromPrevStop: manual(0),
    drivingDistanceFromPrevStop: manual(0)
  });
  payload.reportedData.entries = sortEntries(entries);
  return payload;
}

/** Payload with a new Start/End location entry, sorted into place. */
export function buildAddLocationPayload(timecard: TimeCardModel, stopData: AddStopDialogResult): any {
  const payload = cloneTimecardForUpdate(timecard);
  const entries = ensureReportedEntries(payload, timecard);

  entries.push({
    type: stopData.stopType,
    location: {
      adjusted: {
        value: buildCustomLocationValue(stopData.location),
        source: UpdateSource.Manual
      },
      state: EntryFieldState.Adjusted
    }
  });
  payload.reportedData.entries = sortEntries(entries);
  return payload;
}

/** A CUSTOM location. The point is repeated inside the address, where the server reads it from. */
function buildCustomLocationValue(stopLocation: StopLocation): any {
  const address = stopLocation.address;
  return {
    lat: stopLocation.lat,
    lng: stopLocation.lng,
    locationType: 'CUSTOM',
    address: address ? {
      street: address.street || '',
      house: address.house || '',
      city: address.city || '',
      state: address.state || '',
      country: address.country || '',
      zipCode: address.zipCode || '',
      location: {lat: stopLocation.lat, lng: stopLocation.lng}
    } : null,
    formattedAddress: stopLocation.formattedAddress
  };
}

/** Payload with a new break. The server derives the mirrored reported Break entry from the breaks array. */
export function buildAddBreakPayload(timecard: TimeCardModel, stopData: AddStopDialogResult): any {
  const payload = cloneTimecardForUpdate(timecard);
  const breaks: TimeCardBreakModel[] = [
    ...(resolveFieldValue<TimeCardBreakModel[]>(payload.breaksField) || []),
    {
      startTime: stopData.arrival,
      endTime: stopData.departure,
      breakTypeId: stopData.breakTypeId,
      paid: !!stopData.breakTypePaid
    }
  ];

  payload.breaksField = {
    initial: payload.breaksField?.initial || {value: [], source: UpdateSource.Manual, updatedAt: Date.now()},
    adjusted: buildAdjustedPayload(breaks),
    state: EntryFieldState.Adjusted
  };
  payload.breaks = breaks;
  return payload;
}

/** Payload with new arrival/departure (and duration) on an existing reported entry, or null if it is gone. */
export function buildUpdateStopTimesPayload(timecard: TimeCardModel, entryIndex: number, arrival: number, departure: number): any | null {
  const payload = cloneTimecardForUpdate(timecard);
  const entry = payload.reportedData?.entries?.[entryIndex];
  if (!entry) {
    return null;
  }
  if (entry.startTime) {
    setAdjusted(entry.startTime, arrival);
  }
  if (entry.endTime) {
    setAdjusted(entry.endTime, departure);
  }
  if (entry.duration) {
    setAdjusted(entry.duration, departure - arrival);
  }
  return payload;
}
